import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiDetail, BiSolidEdit } from "react-icons/bi";
import { BsInfoCircle, BsTrash3Fill } from "react-icons/bs";
import { IoArrowBackSharp, IoReceiptSharp } from "react-icons/io5";
import { withRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Button, Col, DropdownItem, DropdownMenu, DropdownToggle, PopoverBody, Row, Table, UncontrolledDropdown, UncontrolledPopover } from "reactstrap";
import swal from "sweetalert";
import {
  convertDataFromSnakeCaseToCamelCase,
  convertToDateString,
  LoadingSpinner
} from "../../constants/const";
import { encryptStorage } from "../../constants/EncryptStorage";
import { GetLanguageString, NoResult, BouncingLoader } from "../helper/Components";
import { GetCustomStyles, GetSelectedLanguage, isUsraRole } from "../helper/Method";
import { viewReceiptPdf } from "../shared/receiptViewer";
import { ApiFinance } from "./ApiFinance";
import { ApiFund } from "./ApiFund";
import { isFinanceDateLocked } from "./financeLockHelper";
import { setGlobalPastMonthLockDay } from "./fiscalYearHelper";
import EditFund from "./EditFund";
import EditOtherIncome from "./income/EditOtherIncome";
import EditOtherIncomeHelpFromMuqamiTanzeem from "./income/EditOtherIncomeHelpFromMuqamiTanzeem";
import EditOtherIncomeLoanFromMarkaz from "./income/EditOtherIncomeLoanFromMarkaz";
import EditOtherIncomeTarbiatiCourseReimbursment from "./income/EditOtherIncomeTarbiatiCourseReimbursment";
import IncomeDetailsModal from "./income/IncomeDetailsModal";
import ManageIncomeFilters from "./ManageIncomeFilters";

const ManageIncome = ({ history }) => {
  const { t } = useTranslation();
  const [selectedTransactionNo, setSelectedTransactionNo] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const [searchBtnClicked, setSearchBtnClicked] = useState(false);
  const [searchedAccountType, setSearchedAccountType] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [detailsTransaction, setDetailsTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lockDay, setLockDay] = useState(null);
  const today = new Date();
  const selectedCategoryLabel =
    selectedTransaction?.category?.label1?.toLowerCase() || "";

  useEffect(() => {
    ApiFinance()
      .GetPastMonthLockStatus()
      .then((res) => {
        if (res?.data?.lockDay !== undefined) {
          setLockDay(res.data.lockDay);
          setGlobalPastMonthLockDay(res.data.lockDay);
        }
      })
      .catch(() => {});
  }, []);

  const [values, setValues] = useState({
    unit: { value: loggedInUnitId, lable: "" },
    unitId: loggedInUnitId,
    startDate: new Date(today.getFullYear(), today.getMonth(), 1),
    endDate: new Date(),
    bankAccountId: 0,
    selectedFinanceCategories: [],
    isSubUnits: false,
    accountType: null,
  });
  useEffect(() => {
    if (isUsraRole()) {
      history.replace("/funds/manageUsraInfaq");
    }
  }, [history]);
  const handleSubUnits = (e) => {
    const { value } = e.target;
    let v = value;
    if (value === "true") v = true;
    else v = false;
    setValues({
      ...values,
      isSubUnits: v,
    });
  };
  const [transactionData, setTransactionData] = useState({
    transactions: [],
    totalAmount: 0,
    accountBalances: []
  });


  const handleSearch = (showLoader = true) => {
    setSearchBtnClicked(true);
    setSearchedAccountType(values.accountType ? values.accountType.value : null);
    if (showLoader) {
      setLoading(true);
    }
    ApiFund()
      .GetInfaqTransactions({
        ...values,
        account_type: values.accountType ? values.accountType.value : null,
        startDate: convertToDateString(values.startDate),
        endDate: convertToDateString(values.endDate),
      })
      .then((res) => {
        const convertedData = convertDataFromSnakeCaseToCamelCase(res.data);
        setTransactionData(convertedData || { transactions: [], totalAmount: 0, accountBalances: [] });

        if (showLoader) {
          setLoading(false);
        }
      })
      .catch(() => {
        if (showLoader) {
          setLoading(false);
        }
      })
      .finally(() => {
        if (showLoader) {
          setLoading(false);
        }
      });
  };

  const getTransactionCategories = (transaction) => {
    if (!transaction) return [];
    const details = (transaction.details || []).filter((item) => (item.amount || 0) < 0);
    if (details.length > 0) {
      const categories = details
        .map((d) => d.category?.label || d.categoryName || d.category?.label1)
        .filter(Boolean);
      const uniqueCategories = Array.from(new Set(categories));
      if (uniqueCategories.length > 0) {
        return uniqueCategories;
      }
    }
    const topCategory = transaction.category?.label || transaction.categoryName || transaction.category?.label1;
    return topCategory ? [topCategory] : [];
  };
  const handleOnDelete = (value, type) => {
    swal({
      title: t("common_delete_message"),
      text: t("common_delete_detail_message"),
      className: GetSelectedLanguage() === "en" ? "" : "ur",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        setLoading(true);
        ApiFund()
          .deleteFund(value, type)
          .then(() => {
            setLoading(false);
            setTransactionData((prev) => ({
              ...prev,
              transactions: (prev.transactions || []).filter((transaction) => {
                const transactionValue =
                  type === "fund"
                    ? transaction.fundId
                    : transaction.transactionNo;
                return String(transactionValue) !== String(value);
              }),
            }));
            handleSearch(false);
          })
          .catch((err) => {
            console.log(err);
            setLoading(false);
          });
      }
    });
  };

  const viewReceipt = (transactionNo, type) =>
    viewReceiptPdf({ transactionNo, type, t }).catch(() => false);

  const { cashInHandIncome, amanatIncome, totalIncome, showCashCard, showAmanatCard, showTotalCard, cardColMd } =
    useMemo(() => {
      const isCashAccount = (code) => code === "cash" || code === "cash_in_hand_account";
      const isAmanatAccount = (code) => code === "amanat" || code === "amanat_account";

      const cashInHand =
        transactionData.cashInHandTotal !== undefined && transactionData.cashInHandTotal !== null
          ? transactionData.cashInHandTotal
          : (transactionData.transactions || [])
              .filter((t) => isCashAccount(t.accountTypeCode))
              .reduce((sum, t) => sum + (t.amount || 0), 0);

      const amanat =
        transactionData.amanatTotal !== undefined && transactionData.amanatTotal !== null
          ? transactionData.amanatTotal
          : (transactionData.transactions || [])
              .filter((t) => isAmanatAccount(t.accountTypeCode))
              .reduce((sum, t) => sum + (t.amount || 0), 0);

      const total = transactionData.totalAmount || 0;

      const isSingleAccountFiltered = searchedAccountType === "cash" || searchedAccountType === "amanat";
      const balances = transactionData.accountBalances || [];
      const unitHasOnlyCash =
        !searchedAccountType && balances.length === 1 && isCashAccount(balances[0].accountTypeCode);
      const unitHasOnlyAmanat =
        !searchedAccountType && balances.length === 1 && isAmanatAccount(balances[0].accountTypeCode);

      const showCash =
        searchedAccountType === "cash" ||
        unitHasOnlyCash ||
        (!searchedAccountType && !unitHasOnlyAmanat);

      const showAmanat =
        searchedAccountType === "amanat" ||
        unitHasOnlyAmanat ||
        (!searchedAccountType && !unitHasOnlyCash);

      const showTotal = !isSingleAccountFiltered && !unitHasOnlyCash && !unitHasOnlyAmanat;
      const colMd = showTotal ? "4" : "12";

      return {
        cashInHandIncome: cashInHand,
        amanatIncome: amanat,
        totalIncome: total,
        showCashCard: showCash,
        showAmanatCard: showAmanat,
        showTotalCard: showTotal,
        cardColMd: colMd,
      };
    }, [transactionData, searchedAccountType]);

  if (isUsraRole()) {
    return null;
  }

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox" style={{ position: "sticky", top: 0, zIndex: 10 }}>
        <ToastContainer
          autoClose={3000}
          bodyClassName={GetSelectedLanguage()}
          closeOnClick
          pauseOnHover={false}
          draggable
        />
        <div className="ibox-title">
          <h5>{<GetLanguageString props="manage_income" />}</h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            {" "}
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>
        <div className="ibox-content">
          <ManageIncomeFilters
            values={values}
            setValues={setValues}
            handleSubUnits={handleSubUnits}
          />

          <Row>
            <Col md="12" className="text-center">
              <div style={{ marginTop: 10 }}>
                <Button
                  color="primary"
                  className="m-l-sm"
                  onClick={handleSearch}
                >
                  {<GetLanguageString props="common_search" />}
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </div>
      {searchBtnClicked && (
        <>
          {!loading ? (
            <div className="ibox">
              <div className="ibox-content">
                <>
                  {openEditModal && (
                    <>
                      {selectedTransaction?.fundId || selectedCategoryLabel === "infaq" ? (
                        <EditFund
                          editValue={selectedTransactionNo}
                          setModal={setOpenEditModal}
                          unitId={values.unit.value}
                          onSuccess={handleSearch}
                        />
                      ) : selectedCategoryLabel === "loan_markaz" ? (
                        <EditOtherIncomeLoanFromMarkaz
                          transactionNo={selectedTransaction?.transactionNo}
                          transaction={selectedTransaction}
                          setModal={setOpenEditModal}
                          unitId={values.unit.value}
                          onSuccess={handleSearch}
                        />
                      ) : selectedCategoryLabel ===
                        "tarbiati_course_reimbursment_from_markaz" ? (
                        <EditOtherIncomeTarbiatiCourseReimbursment
                          transactionNo={selectedTransaction?.transactionNo}
                          transaction={selectedTransaction}
                          setModal={setOpenEditModal}
                          unitId={values.unit.value}
                          onSuccess={handleSearch}
                        />
                      ) : selectedCategoryLabel ===
                        "help_from_muqami_tanzeem" ? (
                        <EditOtherIncomeHelpFromMuqamiTanzeem
                          transactionNo={selectedTransaction?.transactionNo}
                          transaction={selectedTransaction}
                          setModal={setOpenEditModal}
                          unitId={values.unit.value}
                          onSuccess={handleSearch}
                        />
                      ) : (
                        <EditOtherIncome
                          transactionNo={selectedTransaction?.transactionNo}
                          transaction={selectedTransaction}
                          setModal={setOpenEditModal}
                          unitId={values.unit.value}
                          onSuccess={handleSearch}
                        />
                      )}
                    </>
                  )}
                  {openDetailsModal && (
                    <IncomeDetailsModal
                      transaction={detailsTransaction}
                      setModal={setOpenDetailsModal}
                    />
                  )}
                  {(transactionData.transactions || []).length === 0 ? (
                    <NoResult />
                  ) : (
                    <>
                      <Row className="mb-3 align-items-stretch">
                        {/* Cash in Hand Income Card */}
                        {showCashCard && (
                          <Col md={cardColMd} className="mb-2">
                            <div
                              style={{
                                background: "linear-gradient(135deg, #486A84 0%, #39576B 45%, #2D4455 100%)",
                                color: "#ffffff",
                                padding: "12px 16px",
                                borderRadius: "14px",
                                boxShadow: "0 8px 24px rgba(22, 55, 60, 0.14)",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                height: "100%",
                                minHeight: "95px",
                                transition: "all 0.25s ease-in-out",
                                cursor: "default",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = "0 12px 28px rgba(22, 55, 60, 0.2)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "0 8px 24px rgba(22, 55, 60, 0.14)";
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9 }}>
                                    <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
                                    <path d="M4 6v12a2 2 0 0 0 2 2h14v-4" />
                                    <path d="M18 12a2 2 0 0 0 0 4h4v-4z" />
                                  </svg>
                                  <span style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.2px", color: "#ffffff" }}>
                                    {t("cash_in_hand_income") || "Cash in Hand Income"}
                                  </span>
                                </div>
                                <span
                                  style={{
                                    background: "rgba(255, 255, 255, 0.14)",
                                    border: "1px solid rgba(255, 255, 255, 0.18)",
                                    backdropFilter: "blur(10px)",
                                    WebkitBackdropFilter: "blur(10px)",
                                    color: "#ffffff",
                                    borderRadius: "999px",
                                    fontSize: "10px",
                                    fontWeight: "600",
                                    padding: "2px 10px",
                                  }}
                                >
                                  {t("cash_in_hand_account") || "Cash in Hand"}
                                </span>
                              </div>

                              <div style={{ fontSize: "22px", fontWeight: "800", marginTop: "8px", letterSpacing: "-0.4px", color: "#ffffff" }}>
                                {loading ? (
                                  <BouncingLoader />
                                ) : (
                                  `Rs ${new Intl.NumberFormat().format(cashInHandIncome)}`
                                )}
                              </div>
                            </div>
                          </Col>
                        )}

                        {/* Amanat Account Income Card */}
                        {showAmanatCard && (
                          <Col md={cardColMd} className="mb-2">
                            <div
                              style={{
                                background: "linear-gradient(135deg, #35A08E 0%, #237A6E 45%, #1B6258 100%)",
                                color: "#ffffff",
                                padding: "12px 16px",
                                borderRadius: "14px",
                                boxShadow: "0 8px 24px rgba(22, 55, 60, 0.14)",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                height: "100%",
                                minHeight: "95px",
                                transition: "all 0.25s ease-in-out",
                                cursor: "default",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = "0 12px 28px rgba(22, 55, 60, 0.2)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "0 8px 24px rgba(22, 55, 60, 0.14)";
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9 }}>
                                    <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
                                  </svg>
                                  <span style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.2px", color: "#ffffff" }}>
                                    {t("amanat_account_income") || "Amanat Account Income"}
                                  </span>
                                </div>
                                <span
                                  style={{
                                    background: "rgba(255, 255, 255, 0.14)",
                                    border: "1px solid rgba(255, 255, 255, 0.18)",
                                    backdropFilter: "blur(10px)",
                                    WebkitBackdropFilter: "blur(10px)",
                                    color: "#ffffff",
                                    borderRadius: "999px",
                                    fontSize: "10px",
                                    fontWeight: "600",
                                    padding: "2px 10px",
                                  }}
                                >
                                  {t("amanat_account") || "Amanat Account"}
                                </span>
                              </div>

                              <div style={{ fontSize: "22px", fontWeight: "800", marginTop: "8px", letterSpacing: "-0.4px", color: "#ffffff" }}>
                                {loading ? (
                                  <BouncingLoader />
                                ) : (
                                  `Rs ${new Intl.NumberFormat().format(amanatIncome)}`
                                )}
                              </div>
                            </div>
                          </Col>
                        )}

                        {/* Total Amount Card ON THE END - Income */}
                        {showTotalCard && (
                          <Col md={cardColMd} className="mb-2">
                            <div
                              style={{
                                background: "linear-gradient(135deg, #26B88F 0%, #159373 45%, #10725A 100%)",
                                color: "#ffffff",
                                padding: "12px 16px",
                                borderRadius: "14px",
                                boxShadow: "0 8px 24px rgba(22, 55, 60, 0.14)",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                height: "100%",
                                minHeight: "95px",
                                transition: "all 0.25s ease-in-out",
                                cursor: "default",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = "0 12px 28px rgba(22, 55, 60, 0.2)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "0 8px 24px rgba(22, 55, 60, 0.14)";
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9 }}>
                                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                                    <polyline points="17 6 23 6 23 12" />
                                  </svg>
                                  <span style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.2px", color: "#ffffff" }}>
                                    {t("total_income") || <GetLanguageString props="finance_other_income_total_amount" />}
                                  </span>
                                </div>
                                <span
                                  style={{
                                    background: "rgba(255, 255, 255, 0.14)",
                                    border: "1px solid rgba(255, 255, 255, 0.18)",
                                    backdropFilter: "blur(10px)",
                                    WebkitBackdropFilter: "blur(10px)",
                                    color: "#ffffff",
                                    borderRadius: "999px",
                                    fontSize: "10px",
                                    fontWeight: "600",
                                    padding: "2px 10px",
                                  }}
                                >
                                  {t("total_income") || "Income"}
                                </span>
                              </div>
                              <div style={{ fontSize: "22px", fontWeight: "800", marginTop: "8px", letterSpacing: "-0.4px", color: "#ffffff" }}>
                                {loading ? (
                                  <BouncingLoader />
                                ) : (
                                  `Rs ${new Intl.NumberFormat().format(totalIncome)}`
                                )}
                              </div>
                            </div>
                          </Col>
                        )}
                      </Row>

                      <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                        <Table>
                          <thead>
                            <tr style={{ borderBottom: "1px solid black" }}>
                              <th className="text-center">#</th>
                              <th className="text-center">
                                {
                                  <GetLanguageString props="funds_source" />
                                }
                              </th>
                              <th className="text-center">
                                {
                                  <GetLanguageString props="quick_reports3_unit_name" />
                                }
                              </th>

                              <th className="text-center">
                                {<GetLanguageString props="funds_date" />}
                              </th>

                              <th className="text-center">
                                {
                                  <GetLanguageString props="funds_reference" />
                                }
                              </th>
                              <th className="text-center">
                                {
                                  <GetLanguageString props="account_type" />
                                }
                              </th>

                              <th className="text-center">
                                {
                                  <GetLanguageString props="funds_add_fund_amount" />
                                }
                              </th>
                              <th className="text-center">
                                {<GetLanguageString props="transactions_memo" />}
                              </th>
                              <th
                                className="text-center"
                                style={{ minWidth: 70 }}
                              >
                                {<GetLanguageString props="common_action" />}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactionData.transactions.map((transaction, index) => (
                              <tr key={index}>
                                <td className="text-center">{index + 1}</td>
                                {(() => {
                                  const categories = getTransactionCategories(transaction);
                                  if (categories.length === 0) {
                                    return <td className="text-center">-</td>;
                                  }
                                  if (categories.length === 1) {
                                    return (
                                      <td className={`text-center ${GetSelectedLanguage() === "ur" ? "ur" : ""}`}>
                                        <span
                                          style={{
                                            display: "inline-block",
                                            padding: "4px 10px",
                                            fontSize: "12px",
                                            fontWeight: "600",
                                            borderRadius: "12px",
                                            backgroundColor: "#edf2f7",
                                            color: "#4a5568",
                                          }}
                                        >
                                          {categories[0]}
                                        </span>
                                      </td>
                                    );
                                  }
                                  const popoverId = `income-cat-popover-${transaction.transactionNo || index}`;
                                  return (
                                    <td className="text-center">
                                      <span
                                        id={popoverId}
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "5px",
                                          padding: "4px 12px",
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          borderRadius: "12px",
                                          backgroundColor: "#1ab394",
                                          color: "#ffffff",
                                          cursor: "pointer",
                                          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                        }}
                                      >
                                        <BsInfoCircle size={13} style={{ color: "#ffffff" }} />
                                        <GetLanguageString props="multiple_categories" />
                                      </span>
                                      <UncontrolledPopover
                                        trigger="hover focus"
                                        placement="top"
                                        target={popoverId}
                                        style={{
                                          boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
                                          border: "1px solid #e2e8f0",
                                          borderRadius: "8px",
                                        }}
                                      >
                                        <PopoverBody style={{ padding: "10px", backgroundColor: "#ffffff" }}>
                                          <div style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", marginBottom: "6px", letterSpacing: "0.5px" }}>
                                            <GetLanguageString props="multiple_categories" />
                                          </div>
                                          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                            {categories.map((cat, i) => (
                                              <div
                                                key={i}
                                                className={GetSelectedLanguage() === "ur" ? "ur" : ""}
                                                style={{
                                                  fontSize: "12px",
                                                  fontWeight: "600",
                                                  color: "#1e293b",
                                                  padding: "4px 8px",
                                                  backgroundColor: "#f8fafc",
                                                  borderRadius: "4px",
                                                  border: "1px solid #f1f5f9",
                                                }}
                                              >
                                                {cat}
                                              </div>
                                            ))}
                                          </div>
                                        </PopoverBody>
                                      </UncontrolledPopover>
                                    </td>
                                  );
                                })()}

                                <td className="text-center ur">
                                  {transaction.unitName}
                                </td>
                                <td className="text-center">
                                  {moment(transaction.date, "YYYY-MM-DD").format(
                                    "DD/MM/YYYY",
                                  )}
                                </td>

                                <td className="text-center">
                                  {transaction.reference
                                    ?.trim()
                                    .split(/\s+/)
                                    .pop() || ""}
                                </td>
                                <td className="text-center">
                                  {transaction.accountTypeCode === "cash_in_hand_account"
                                    ? t("cash_in_hand_account")
                                    : transaction.accountTypeCode === "amanat_account"
                                      ? t("amanat_account")
                                      : "-"}
                                </td>

                                <td
                                  className={"text-center "}
                                  style={{
                                    color:
                                      transaction.amount < 0 ? "red" : "green",
                                  }}
                                >
                                  <strong>
                                    {new Intl.NumberFormat().format(
                                      transaction.amount,
                                    )}
                                  </strong>
                                </td>
                                 <td>
                                   {transaction.memo ||
                                     transaction.lineMemo ||
                                     transaction.details?.[0]?.memo ||
                                     transaction.details?.[0]?.lineMemo ||
                                     "-"}
                                 </td>
                                {transaction.isSyncedWithFinanceSoftware ? (
                                  <td className="text-center">
                                    <UncontrolledDropdown>
                                      <DropdownToggle
                                        className="btn btn-default"
                                        tag="button"
                                        style={{ padding: "4px 10px" }}
                                      >
                                        <GetLanguageString props="common_action" />
                                        &nbsp;<b className="caret m-l-xs" />
                                      </DropdownToggle>
                                      <DropdownMenu right container="body" style={{ zIndex: 99999 }}>{transaction.details && transaction.details.length > 0 && (
                                        <DropdownItem
                                          onClick={() => {
                                            setDetailsTransaction(transaction);
                                            setOpenDetailsModal(true);
                                          }}
                                        >
                                          <BiDetail color="#18A689" size={18} />
                                          &nbsp;&nbsp;<GetLanguageString props="finance_other_income_entry_details" />
                                        </DropdownItem>
                                      )}<DropdownItem
                                          onClick={() =>
                                            viewReceipt(
                                              transaction.transactionNo,
                                              transaction.category?.label1?.toLowerCase() === "infaq"
                                                ? "infaq"
                                                : "otherIncome"
                                            )
                                          }>
                                          <IoReceiptSharp color="#5b7cfa" size={18} />
                                          &nbsp;&nbsp;<GetLanguageString props="common_view_receipt" />
                                        </DropdownItem>
                                          {transaction.unitId === loggedInUnitId &&
                                            !isFinanceDateLocked(transaction.date, lockDay) &&
                                            transaction?.category?.label1?.toLowerCase() !== "transfers" &&
                                            transaction?.category?.label1?.toLowerCase() !== "funds_transfer" &&
                                            transaction?.category?.label1?.toLowerCase() !== "fund_transfer" &&
                                            !(transaction.category?.label || transaction.categoryName || "").toLowerCase().includes("transfer") && (
                                            <DropdownItem
                                              onClick={() => {
                                                setSelectedTransaction(transaction);
                                                setSelectedTransactionNo(
                                                  transaction.fundId || transaction.transactionNo
                                                );
                                                setOpenEditModal(true);
                                              }}
                                            >
                                              <BiSolidEdit color="#3588a2ff" size={18} />
                                              &nbsp;&nbsp;<GetLanguageString props="common_edit" />
                                            </DropdownItem>
                                          )}
                                         {
                                           transaction.unitId === loggedInUnitId &&
                                           !isFinanceDateLocked(transaction.date, lockDay) &&
                                           (transaction?.accountTypeCode ?? "").toLowerCase() !== "amanat_account" &&
                                           transaction?.category?.label1?.toLowerCase() !== "help_from_muqami_tanzeem" && (
                                             <DropdownItem
                                               onClick={() =>
                                                 handleOnDelete(
                                                   transaction.fundId ?? transaction.transactionNo,
                                                   transaction.fundId ? "fund" : "transaction"
                                                 )
                                               }
                                             >
                                               <BsTrash3Fill color="#bd3826ff" size={18} />
                                               &nbsp;&nbsp;<GetLanguageString props="common_delete" />
                                             </DropdownItem>
                                           )
                                         }

                                      </DropdownMenu>
                                    </UncontrolledDropdown>
                                  </td>
                                ) : (
                                  <td className="text-center">-</td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </>
                  )}
                </>
              </div>
            </div>
          ) : (
            LoadingSpinner()
          )}
        </>
      )}
    </div>
  );
};

export default withRouter(ManageIncome);
